/**
 * Navigation guard functions - reusable conditional checks
 * Eliminates duplication of common navigation logic patterns
 */

import {
  KYCStatus,
  EntityTypeEnum,
  RegtankOnboardingStatusEnum,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { getThanksPageUrl } from "@funded-here-interface/investor-onboarding/src/Screens/ThanksPage/ThanksPageUtils";
import { ThanksPageType } from "@funded-here-interface/investor-onboarding/src/constants/enums";
import { UserDetail } from "./types";
/**
 * Check if user should be redirected to Regtank response page
 */
export const checkRegtankResponseRedirect = (
  userDetail: UserDetail,
  path?: string
): string | undefined => {
  const location = path;
  const isDirectorAuthRep =
    userDetail?.boardOfDirectors && userDetail?.boardOfDirectors.length > 0
      ? userDetail?.boardOfDirectors?.find(
          (x: any) => x.isAuthRep && x.email === userDetail.email
        )
      : false;

  const hasValidRegtankRequest =
    userDetail?.regtankOnboardingFeatureFlag &&
    userDetail?.regtankRequest &&
    userDetail?.regtankRequest.length > 0 &&
    userDetail?.pendingOnBoardingAs !== null;

  const isIndividualPendingApproval =
    hasValidRegtankRequest &&
    userDetail?.entityType === EntityTypeEnum.INDIVIDUAL &&
    !userDetail?.declarationAISubmitted &&
    userDetail?.kycStatus !== KYCStatus.PENDING_RESUBMISSION &&
    location !== "/regtank-response";

  const isDirectorNotYetApproved =
    (userDetail?.pendingOnBoardingAs === Persona.MERCHANT_DIRECTOR ||
      userDetail?.pendingOnBoardingAs === Persona.INVESTOR_DIRECTOR ||
      userDetail?.pendingOnBoardingAs === Persona.ISSUER_DIRECTOR) &&
    userDetail?.kycStatus !== KYCStatus.APPROVED;

  const isAuthRepKYCApproved =
    userDetail?.pendingOnBoardingAs === "auth-rep" &&
    userDetail?.kycStatus === KYCStatus.APPROVED &&
    !isDirectorAuthRep &&
    location !== "/regtank-response";

  if (
    hasValidRegtankRequest &&
    (isIndividualPendingApproval ||
      isDirectorNotYetApproved ||
      isAuthRepKYCApproved)
  ) {
    return "/regtank-response";
  }

  return undefined;
};

/**
 * Check if user needs eKYC completion (Regtank onboarding)
 * Returns eKYC thanks page if user hasn't completed Regtank request
 */
export const checkEkycCompletion = (
  userDetail: UserDetail
): string | undefined => {
  if (userDetail?.regtankOnboardingFeatureFlag === true) {
    if (!userDetail?.regtankRequest) {
      return getThanksPageUrl(ThanksPageType.EKYC);
    }
  }
  return undefined;
};

/**
 * Check if user needs eKYC for auth reps specifically
 * Slightly different logic - checks isAuthRep flag
 */
export const checkAuthRepEkycCompletion = (
  userDetail: UserDetail
): string | undefined => {
  if (!userDetail?.regtankRequest && userDetail?.isAuthRep) {
    return getThanksPageUrl(ThanksPageType.EKYC);
  }
  return undefined;
};

/**
 * Calculate if user has credit limit
 * Repeated calculation across multiple functions
 */
export const hasCreditLimit = (userDetail: UserDetail): boolean => {
  return (userDetail?.orgRole?.[0]?.creditLimit ?? 0) > 0;
};

/**
 * Check for pending resubmission status
 * Returns appropriate business profile path based on context
 */
export const checkPendingResubmission = (
  userDetail: UserDetail,
  profilePath: string
): string | undefined => {
  if (userDetail?.kycStatus === KYCStatus.PENDING_RESUBMISSION) {
    return profilePath;
  }
  return undefined;
};

/**
 * Check if user has email history (indicates resubmission)
 * Email history is created when admin sends rejection/resubmission email
 */
export const hasEmailHistory = (userDetail: UserDetail): boolean => {
  return userDetail?.hasEmailHistory === true;
};

/**
 * Check if KYC is pending approval
 * Returns appropriate thanks page based on whether this is a resubmission
 */
export const checkPendingApproval = (
  userDetail: UserDetail
): string | undefined => {
  if (userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL) {
    // If there's email history, this is a resubmission
    if (hasEmailHistory(userDetail)) {
      return getThanksPageUrl(ThanksPageType.RESUBMITTED);
    }
    return getThanksPageUrl(ThanksPageType.ONBOARDING_SUPPLIER);
  }
  return undefined;
};

/**
 * Check if organization is undefined (new user)
 * Returns true if user hasn't created an organization yet
 */
export const isNewUser = (userDetail: UserDetail): boolean => {
  return userDetail?.organization === undefined;
};

/**
 * Check if user is approved and not suspended
 * Common check for director paths
 */
export const isApprovedAndActive = (userDetail: UserDetail): boolean => {
  return (
    userDetail?.kycStatus === KYCStatus.APPROVED && !userDetail?.isSuspended
  );
};
